import React from 'react';
import { Button, Dropdown } from 'antd';
import { FAMILY_FILE_TYPES } from 'services/family.service';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';
import { FAMILY_BRANCH_MAP } from '../familyApplicationConstants';
import { ChevronDown } from '@carbon/icons-react';

/**
 * A button component that provides a link to download a blank form
 * @param {Object} props
 * @param {string} props.fileType - The file type code to determine which form to download
 * @param {number} [props.branchId] - Optional branch ID for military-specific forms
 * @param {string} [props.buttonText] - Optional custom button text (defaults to "Download blank form")
 * @param {Function} [props.onClick] - Optional click handler that runs before navigation
 */
export default function FamilyAppBlankFormDownloadButton({
  fileType,
  branchId,
  buttonText = 'Download blank form',
  onClick,
}) {
  // Special handling for SPOUSE_WORK - show dropdown with multiple options
  if (fileType === FAMILY_FILE_TYPES.SPOUSE_WORK?.code) {
    const items = [
      {
        key: 'employment',
        label: (
          <a
            href="https://short.ccaoa.org/EVF"
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              e?.stopPropagation?.();
              onClick?.(e);
            }}
          >
            Employment Verification Form
          </a>
        ),
      },
      {
        key: 'parental-leave',
        label: (
          <a
            href="https://short.ccaoa.org/MLV"
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              e?.stopPropagation?.();
              onClick?.(e);
            }}
          >
            Parental Leave Form
          </a>
        ),
      },
    ];

    return (
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button htmlType="button" onClick={(e) => e.stopPropagation()}>
          Download blank forms <ChevronDown className="mb-[-1px] ml-[-1px] mr-[-2px]" />
        </Button>
      </Dropdown>
    );
  }

  // Standard single-link button for all other file types
  const downloadLink = getBlankFormDownloadLink(fileType, branchId);

  if (!downloadLink) {
    return null;
  }

  return (
    <a
      href={downloadLink}
      onClick={(e) => {
        e?.stopPropagation?.();
        onClick?.(e);
      }}
      target="_blank"
      rel="noreferrer"
    >
      <Button htmlType="button">{buttonText}</Button>
    </a>
  );
}

/**
 * Gets the download link for a blank form based on file type and optional branch ID
 * @param {string} fileType - The file type code
 * @param {number} branchId - Optional branch ID for military-specific forms
 * @returns {string|null} The download URL or null if no form is available
 */
export function getBlankFormDownloadLink(fileType, branchId) {
  switch (fileType) {
    case FAMILY_FILE_TYPES.CHILD_CYS?.code:
      return 'https://info.childcareaware.org/hubfs/Statement%20of%20Non-Availability%20%5BFillableNov2024%5D.pdf';

    case FAMILY_FILE_TYPES.PROVIDER_RATES?.code:
      // Branch-specific provider rates forms
      if (branchId === FAMILY_BRANCH_MAP.army.id) {
        return 'https://www.childcareaware.org/wp-content/uploads/2021/03/3.25.20-PCVF.pdf';
      }
      return 'https://info.childcareaware.org/hubfs/AFMC%20Provider%20Cost%20Verification%20Form_Apr2024%203.pdf';

    case FAMILY_FILE_TYPES.SPOUSE_WORK?.code:
      return 'https://short.ccaoa.org/EVF';

    case PROVIDER_FILE_TYPES.W9?.code:
      return 'https://info.childcareaware.org/hubfs/W-9%202024.pdf';

    default:
      return null;
  }
}
